import {
  AuditReportsAudioStatusDto,
  AuditReportsGipReportMatchedScheduleDto,
  AuditReportsGipReportResultDto,
  AuditReportsGipReportScheduleDto,
} from '@mi-front/api-v2';
import { ChipNavigationMui, ChipNavigationProps } from '@mi-front/ui';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DateRangeIcon from '@mui/icons-material/DateRange';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import WarningIcon from '@mui/icons-material/Warning';
import { Box } from '@mui/material';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { GipReportFutureSchedulesView } from '../gip-report/preview/components/result/GipReportFutureSchedulesView';
import { GipReportMatchedSchedulesView } from '../gip-report/preview/components/result/GipReportMatchedSchedulesView';
import { GipReportMissingMaterialsView } from '../gip-report/preview/components/result/GipReportMissingMaterialsView';
import { GipReportUnmatchedSchedulesView } from '../gip-report/preview/components/result/GipReportUnmatchedSchedulesView';
import { GipReportUnprocessedSchedulesView } from '../gip-report/preview/components/result/GipReportUnprocessedSchedulesView';
import { countGipMissingMaterials } from './count-gip-missing-materials';
import {
  gipReportChipTabLongFutureMessage,
  gipReportChipTabLongMissingMaterialsMessage,
  gipReportChipTabLongToBeProcessedMessage,
  gipReportChipTabLongWithMatchesMessage,
  gipReportChipTabLongWithoutMatchesMessage,
} from './gip-report-chip-navigation-messages';

export interface GipReportChipNavigationProps {
  // Result object with schedule data
  result: AuditReportsGipReportResultDto;

  // Optional custom view components (defaults to GIP report views)
  MatchedSchedulesView?: React.ComponentType<{
    schedules: AuditReportsGipReportMatchedScheduleDto[];
  }>;
  UnmatchedSchedulesView?: React.ComponentType<{
    schedules: AuditReportsGipReportScheduleDto[];
  }>;
  UnprocessedSchedulesView?: React.ComponentType<{
    schedules: AuditReportsGipReportScheduleDto[];
  }>;
  FutureSchedulesView?: React.ComponentType<{
    schedules: AuditReportsGipReportScheduleDto[];
  }>;
  MissingMaterialsView?: React.ComponentType<{
    audiosStatusByStation: {
      [key: string]: AuditReportsAudioStatusDto[];
    };
  }>;

  // Optional additional chips to add at the beginning
  additionalChips?: ChipNavigationProps['chips'];

  // Optional action component to display at the top right of the navigation
  actionComponent?: React.ReactNode;

  // Configuration
  defaultActiveChip?: string;

  /** When true, tab labels include a schedules prefix (e.g. “Schedules sin matches” instead of “Sin matches”). */
  useLongLabel?: boolean;
}

export function GipReportChipNavigation({
  result,
  MatchedSchedulesView = GipReportMatchedSchedulesView,
  UnmatchedSchedulesView = GipReportUnmatchedSchedulesView,
  UnprocessedSchedulesView = GipReportUnprocessedSchedulesView,
  FutureSchedulesView = GipReportFutureSchedulesView,
  MissingMaterialsView = GipReportMissingMaterialsView,
  additionalChips = [],
  actionComponent,
  defaultActiveChip = 'with-matches',
  useLongLabel = false,
}: GipReportChipNavigationProps) {
  const { $t, formatMessage } = useIntl();

  const {
    matchedSchedules,
    unmatchedSchedules,
    unprocessedSchedules,
    futureSchedules,
    audiosStatusByStation,
  } = result;

  const missingMaterialsCount = countGipMissingMaterials(audiosStatusByStation);

  const standardChips: ChipNavigationProps['chips'] = [
    {
      value: 'with-matches',
      label: `${
        useLongLabel
          ? formatMessage(gipReportChipTabLongWithMatchesMessage)
          : $t({
              id: 'audit.gipReport.result.tabs.withMatches',
              defaultMessage: 'Con matches',
            })
      } (${matchedSchedules.length})`,
      icon: <CheckIcon />,
      content: <MatchedSchedulesView schedules={matchedSchedules} />,
    },
    {
      value: 'without-matches',
      label: `${
        useLongLabel
          ? formatMessage(gipReportChipTabLongWithoutMatchesMessage)
          : $t({
              id: 'audit.gipReport.result.tabs.withoutMatches',
              defaultMessage: 'Sin matches',
            })
      } (${unmatchedSchedules.length})`,
      icon: <CloseIcon />,
      content: <UnmatchedSchedulesView schedules={unmatchedSchedules} />,
    },
    {
      value: 'to-be-processed',
      label: `${
        useLongLabel
          ? formatMessage(gipReportChipTabLongToBeProcessedMessage)
          : $t({
              id: 'audit.gipReport.result.tabs.toBeProcessed',
              defaultMessage: 'Por procesar',
            })
      } (${unprocessedSchedules.length})`,
      icon: <HourglassEmptyIcon />,
      content: <UnprocessedSchedulesView schedules={unprocessedSchedules} />,
    },
    {
      value: 'future',
      label: `${
        useLongLabel
          ? formatMessage(gipReportChipTabLongFutureMessage)
          : $t({
              id: 'audit.gipReport.result.tabs.future',
              defaultMessage: 'Futuros',
            })
      } (${futureSchedules.length})`,
      icon: <DateRangeIcon />,
      content: <FutureSchedulesView schedules={futureSchedules} />,
    },
    // Only show missing materials tab if there are missing materials
    ...(missingMaterialsCount > 0
      ? [
          {
            value: 'missing-materials',
            label: `${
              useLongLabel
                ? formatMessage(gipReportChipTabLongMissingMaterialsMessage)
                : $t({
                    id: 'audit.gipReport.result.tabs.missingMaterials',
                    defaultMessage: 'Materiales faltantes',
                  })
            } (${missingMaterialsCount})`,
            icon: <WarningIcon />,
            color: 'warning' as const,
            content: <MissingMaterialsView audiosStatusByStation={audiosStatusByStation} />,
          },
        ]
      : []),
  ];

  const chips = [...additionalChips, ...standardChips];

  return (
    <ChipNavigationMui
      chips={chips}
      defaultActiveChip={defaultActiveChip}
      header={actionComponent}
      ContentWrapper={useCallback(
        (p: { children?: React.ReactNode }) => (
          <Box mt={2} {...p} />
        ),
        [],
      )}
    />
  );
}
